/**
 * Accounting Friendly Layer API service
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../../../shared/api/axiosInstance';
import { buildParams } from '../../../shared/api/buildParams';
import type { DocumentDistributePayload, DocumentDistributionItem } from '../../../shared/documents';
import type {
  Invoice, InvoiceCreate, InvoiceLineCreate, InvoiceUpdate, ListInvoicesParams,
  InvoiceShipmentLink, InvoiceShipmentLinkPayload,
  InvoiceListFilterParams, InvoiceAggregatesResponse,
  BulkInvoiceActionResult,
  BulkBillActionResult,
  RevenueRecognitionSchedule, DeferredRevenueRollforward,
  Payment, PaymentCreate, PaymentAllocationCreate, ListPaymentsParams, PaymentsListResponse,
  DocumentPaymentsParams, InvoicePayment, BillPayment,
  Bill, BillCreate, BillLineCreate, BillUpdate, BillShipmentLink, BillLineCategoryAssignment, ListBillsParams,
  BillThreeWayMatch,
  UnbilledReceiptSummary,
  LandedCostCategory,
  TaxRate, TaxRateCreate, TaxRateUpdate, ListTaxRatesParams,
  TaxDeterminationRule, TaxDeterminationRuleCreate, TaxDeterminationRuleUpdate,
  ListTaxDeterminationRulesParams, TaxDeterminationResolveRequest, TaxDeterminationResult,
  BankAccount, BankAccountCreateRequest, BankAccountUpdateRequest, ListBankAccountsParams,
  RecurringTemplate, CreateRecurringFromInvoice, CreateRecurringFromBill, UpdateRecurring, ListRecurringParams,
  PaginatedResponse, DataResponse,
  BankDeposit, BankDepositCreate, BankDepositUpdate,
  BankDepositLineCreate, BankDepositValidateResult, BankDepositPostRequest,
  BankDepositVoidRequest, ListBankDepositsParams,
  PendingReceipt, ListPendingReceiptsParams,
  BankTransfer, BankTransferCreate, BankTransferVoidRequest, ListBankTransfersParams,
  BankFeePostRequest, ConfirmDepositMatchRequest, DepositRoutingDefault,
  PayerBalance, PayerReimbursementLedgerEntry, PayerTransactionsResponse,
  PayerEntityTable, ReimbursementClearRequest, ReimbursementClearBatchRequest,
  ListPayerBalancesParams, ListPayerTransactionsParams,
} from '../types';

const API_BASE = '/api/v1/accounting';

// The query builder is shared: see shared/api/buildParams.ts for why it
// iterates the params object rather than naming each field.

// ============== Invoices ==============

export const invoiceApi = {
  list: async (params?: ListInvoicesParams): Promise<PaginatedResponse<Invoice>> =>
    apiGet<PaginatedResponse<Invoice>>(`${API_BASE}/invoices${buildParams(params)}`),

  /**
   * Aggregates over the filtered-but-unpaged set (ui-list-pages.md §11).
   * Takes InvoiceListFilterParams, so a page window cannot be passed in.
   */
  aggregates: async (params?: InvoiceListFilterParams): Promise<InvoiceAggregatesResponse> =>
    apiGet<InvoiceAggregatesResponse>(`${API_BASE}/invoices/aggregates${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<Invoice>> =>
    apiGet<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}`),

  create: async (data: InvoiceCreate): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices`, data),

  update: async (id: string, data: InvoiceUpdate): Promise<DataResponse<Invoice>> =>
    apiPut<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/invoices/${id}`);
  },

  // TMS B1/B2 sell-side: the freight shipment(s) this AR invoice bills. Both
  // storage arms — the invoice's own freight_shipment_id and the consolidated
  // link through the shipments' sell charges. Forwarder-persona gated (404 for
  // a non-forwarder tenant), so only call it behind usePersona.
  listShipments: async (id: string): Promise<DataResponse<InvoiceShipmentLink[]>> =>
    apiGet<DataResponse<InvoiceShipmentLink[]>>(`${API_BASE}/invoices/${id}/shipments`),

  // Set or clear the SINGLE-shipment arm. The consolidated arm has no setter
  // here: it is written by "Invoice this shipment" on the shipments side.
  linkShipment: async (
    id: string, data: InvoiceShipmentLinkPayload,
  ): Promise<DataResponse<Invoice>> =>
    apiPatch<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/shipment-link`, data),

  // Release ONE shipment from this invoice, whichever arm binds them. The route
  // answers with per-arm counters; the UI refetches the list rather than reading
  // them back, so the shared apiDelete (which discards the body) is enough.
  unlinkShipment: async (id: string, shipmentId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/invoices/${id}/shipments/${shipmentId}`);
  },

  send: async (id: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/send`),

  void: async (id: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/void`),

  // Bulk send/void: bounded (≤100 ids) batch actions returning per-id succeeded /
  // skipped{ id, reason } so the caller can report partial results. The body field
  // is `ids` because the route's model extends the shared `BulkIdsRequest`, which
  // is how it states that these actions cannot take a filter set — each posts a
  // journal entry per invoice (InvoicesListPage's bulk bar carries the verdict).
  bulkSend: async (invoiceIds: string[]): Promise<DataResponse<BulkInvoiceActionResult>> =>
    apiPost<DataResponse<BulkInvoiceActionResult>>(`${API_BASE}/invoices/bulk-send`, {
      ids: invoiceIds,
    }),

  bulkVoid: async (invoiceIds: string[]): Promise<DataResponse<BulkInvoiceActionResult>> =>
    apiPost<DataResponse<BulkInvoiceActionResult>>(`${API_BASE}/invoices/bulk-void`, {
      ids: invoiceIds,
    }),

  // Write off an uncollectible invoice: posts DR Bad Debt / CR AR and flips the
  // invoice to written_off (relieving AR aging + dunning). Requires a reason.
  writeOff: async (id: string, reason: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/write-off`, { reason }),

  clone: async (id: string): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/clone`),

  createCreditNote: async (
    id: string,
    options?: { lines?: InvoiceLineCreate[]; reason?: string },
  ): Promise<DataResponse<Invoice>> =>
    apiPost<DataResponse<Invoice>>(`${API_BASE}/invoices/${id}/credit-note`, {
      ...(options?.lines ? { lines: options.lines } : {}),
      ...(options?.reason ? { reason: options.reason } : {}),
    }),

  // Deferred-revenue [33]: the recognition schedule rows for this invoice.
  revenueRecognitionSchedule: async (id: string): Promise<DataResponse<RevenueRecognitionSchedule[]>> =>
    apiGet<DataResponse<RevenueRecognitionSchedule[]>>(
      `${API_BASE}/invoices/${id}/revenue-recognition-schedule`,
    ),
};

// ============== Deferred-revenue reports ==============

export const deferredRevenueApi = {
  rollforward: async (
    startDate: string,
    endDate: string,
  ): Promise<DataResponse<DeferredRevenueRollforward>> =>
    apiGet<DataResponse<DeferredRevenueRollforward>>(
      `${API_BASE}/reports/deferred-revenue-rollforward?start_date=${startDate}&end_date=${endDate}`,
    ),
};

// ============== Payments ==============

export const paymentApi = {
  list: async (params?: ListPaymentsParams): Promise<PaymentsListResponse> =>
    apiGet<PaymentsListResponse>(`${API_BASE}/payments${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<Payment>> =>
    apiGet<DataResponse<Payment>>(`${API_BASE}/payments/${id}`),

  // The document payment history accumulates a row per collection (an
  // installment plan can run to dozens), so it is server-paged like every other
  // Shape A surface — the response carries the FILTERED total for the pager.
  listForInvoice: async (
    invoiceId: string,
    params?: DocumentPaymentsParams,
  ): Promise<PaginatedResponse<InvoicePayment>> =>
    apiGet<PaginatedResponse<InvoicePayment>>(
      `${API_BASE}/invoices/${invoiceId}/payments${buildParams(params)}`,
    ),

  listForBill: async (
    billId: string,
    params?: DocumentPaymentsParams,
  ): Promise<PaginatedResponse<BillPayment>> =>
    apiGet<PaginatedResponse<BillPayment>>(
      `${API_BASE}/bills/${billId}/payments${buildParams(params)}`,
    ),

  create: async (data: PaymentCreate): Promise<DataResponse<Payment>> =>
    apiPost<DataResponse<Payment>>(`${API_BASE}/payments`, data),

  void: async (id: string): Promise<DataResponse<Payment>> =>
    apiPost<DataResponse<Payment>>(`${API_BASE}/payments/${id}/void`),

  // Apply an existing vendor (made) payment to open bills after creation.
  allocate: async (
    id: string,
    allocations: PaymentAllocationCreate[],
  ): Promise<DataResponse<Payment>> =>
    apiPost<DataResponse<Payment>>(`${API_BASE}/payments/${id}/allocations`, { allocations }),

  distribute: async (
    id: string,
    payload: DocumentDistributePayload,
  ): Promise<DataResponse<DocumentDistributionItem>> =>
    apiPost<DataResponse<DocumentDistributionItem>>(`${API_BASE}/payments/${id}/distribute`, payload),
};

// ============== Bills ==============

export const billApi = {
  list: async (params?: ListBillsParams): Promise<PaginatedResponse<Bill>> =>
    apiGet<PaginatedResponse<Bill>>(`${API_BASE}/bills${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<Bill>> =>
    apiGet<DataResponse<Bill>>(`${API_BASE}/bills/${id}`),

  create: async (data: BillCreate): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills`, data),

  update: async (id: string, data: BillUpdate): Promise<DataResponse<Bill>> =>
    apiPut<DataResponse<Bill>>(`${API_BASE}/bills/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/bills/${id}`);
  },

  submit: async (id: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/submit`),

  // Bulk submit/approve (J8-01): bounded (≤100 ids) batch actions returning
  // per-id succeeded / skipped{ id, reason } so the caller can report partial
  // results. Deliberately no accept_variance — a three-way-match hold is a
  // per-bill decision, so a held bill comes back skipped with its reason.
  bulkSubmit: async (billIds: string[]): Promise<DataResponse<BulkBillActionResult>> =>
    apiPost<DataResponse<BulkBillActionResult>>(`${API_BASE}/bills/bulk-submit`, {
      bill_ids: billIds,
    }),

  bulkApprove: async (billIds: string[]): Promise<DataResponse<BulkBillActionResult>> =>
    apiPost<DataResponse<BulkBillActionResult>>(`${API_BASE}/bills/bulk-approve`, {
      bill_ids: billIds,
    }),

  approve: async (id: string, acceptVariance = false): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(
      `${API_BASE}/bills/${id}/approve`,
      // accept_variance is the explicit reviewer decision to book an
      // out-of-tolerance three-way-match variance (BILL_THREE_WAY_MATCH_HOLD).
      acceptVariance ? { accept_variance: true } : undefined
    ),

  // F-07: the PO/receipts/bill comparison for THIS bill, so the variance the
  // goods receipt raised is on the payable the AP clerk actually approves.
  threeWayMatch: async (id: string): Promise<DataResponse<BillThreeWayMatch>> =>
    apiGet<DataResponse<BillThreeWayMatch>>(`${API_BASE}/bills/${id}/three-way-match`),

  void: async (id: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/void`),

  clone: async (id: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/clone`),

  // The AP mirror of invoiceApi.createCreditNote: a draft vendor credit against
  // this bill, for the over-billing / pricing-dispute case that has no physical
  // return. Omitting `lines` credits the whole bill.
  createVendorCredit: async (
    id: string,
    options?: { lines?: BillLineCreate[]; reason?: string },
  ): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/${id}/vendor-credit`, {
      ...(options?.lines ? { lines: options.lines } : {}),
      ...(options?.reason ? { reason: options.reason } : {}),
    }),

  // T20B: link/unlink a bill to an inbound freight shipment + landed-cost category.
  linkShipment: async (id: string, data: BillShipmentLink): Promise<DataResponse<Bill>> =>
    apiPatch<DataResponse<Bill>>(`${API_BASE}/bills/${id}/shipment-link`, data),

  // GAP-1: set the per-line landed-cost category on a mixed-charge forwarder
  // invoice. Like the shipment link this is categorisation metadata, so it works
  // on approved/paid bills; lines not listed are left untouched.
  setLineLandedCostCategories: async (
    id: string,
    assignments: BillLineCategoryAssignment[],
  ): Promise<DataResponse<Bill>> =>
    apiPatch<DataResponse<Bill>>(`${API_BASE}/bills/${id}/line-landed-cost-categories`, {
      assignments,
    }),

  // Invoice-first AP: manually link a DRAFT bill to a PO / goods receipt(s) so it
  // relieves GRNI (the unmatched-case fallback for the auto-match at ingestion).
  linkPurchaseOrder: async (id: string, goodsReceiptIds: string[]): Promise<DataResponse<Bill>> =>
    apiPatch<DataResponse<Bill>>(`${API_BASE}/bills/${id}/po-link`, { goods_receipt_ids: goodsReceiptIds }),

  // P2P-1: build a draft bill from one confirmed goods receipt (GR "Create Bill" CTA).
  fromReceipt: async (goodsReceiptId: string): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/from-receipt`, { goods_receipt_id: goodsReceiptId }),

  // P2P-1: assemble one draft bill from several confirmed, unbilled receipts.
  fromReceipts: async (goodsReceiptIds: string[]): Promise<DataResponse<Bill>> =>
    apiPost<DataResponse<Bill>>(`${API_BASE}/bills/from-receipts`, { goods_receipt_ids: goodsReceiptIds }),

  // P2P-1: confirmed receipts for a vendor not yet on any bill (picker source).
  unbilledReceipts: async (
    vendorId: string,
    companyId?: string,
  ): Promise<{ items: UnbilledReceiptSummary[] }> =>
    apiGet<{ items: UnbilledReceiptSummary[] }>(
      `${API_BASE}/unbilled-receipts${buildParams({ vendor_id: vendorId, company_id: companyId })}`,
    ),
};

// ============== Landed Cost Categories (T20B) ==============

export const landedCostCategoryApi = {
  list: async (params?: { is_active?: boolean }): Promise<{ items: LandedCostCategory[] }> =>
    apiGet<{ items: LandedCostCategory[] }>(`${API_BASE}/landed-cost-categories${buildParams(params)}`),
};

// ============== Tax Rates ==============

export const taxRateApi = {
  // The route answers `{"data": [...]}` with no `total` — it returns the whole
  // company-scoped set (Shape B). Typing it `PaginatedResponse` made `data.total`
  // read as a number and it was always `undefined`.
  list: async (params?: ListTaxRatesParams): Promise<DataResponse<TaxRate[]>> =>
    apiGet<DataResponse<TaxRate[]>>(`${API_BASE}/tax-rates${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<TaxRate>> =>
    apiGet<DataResponse<TaxRate>>(`${API_BASE}/tax-rates/${id}`),

  create: async (data: TaxRateCreate): Promise<DataResponse<TaxRate>> =>
    apiPost<DataResponse<TaxRate>>(`${API_BASE}/tax-rates`, data),

  update: async (id: string, data: TaxRateUpdate): Promise<DataResponse<TaxRate>> =>
    apiPut<DataResponse<TaxRate>>(`${API_BASE}/tax-rates/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/tax-rates/${id}`);
  },

  initializeDefaults: async (): Promise<DataResponse<TaxRate[]>> =>
    apiPost<DataResponse<TaxRate[]>>(`${API_BASE}/tax-rates/initialize-defaults`),
};

// ============== Tax Determination Rules ==============

export const taxDeterminationApi = {
  list: async (params?: ListTaxDeterminationRulesParams): Promise<{ data: TaxDeterminationRule[] }> =>
    apiGet<{ data: TaxDeterminationRule[] }>(`${API_BASE}/tax-determination-rules${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<TaxDeterminationRule>> =>
    apiGet<DataResponse<TaxDeterminationRule>>(`${API_BASE}/tax-determination-rules/${id}`),

  create: async (data: TaxDeterminationRuleCreate): Promise<DataResponse<TaxDeterminationRule>> =>
    apiPost<DataResponse<TaxDeterminationRule>>(`${API_BASE}/tax-determination-rules`, data),

  update: async (id: string, data: TaxDeterminationRuleUpdate): Promise<DataResponse<TaxDeterminationRule>> =>
    apiPut<DataResponse<TaxDeterminationRule>>(`${API_BASE}/tax-determination-rules/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/tax-determination-rules/${id}`);
  },

  resolve: async (data: TaxDeterminationResolveRequest): Promise<DataResponse<TaxDeterminationResult>> =>
    apiPost<DataResponse<TaxDeterminationResult>>(`${API_BASE}/tax-determination/resolve`, data),
};

// ============== Bank Accounts ==============

export const bankAccountApi = {
  list: async (params?: ListBankAccountsParams): Promise<PaginatedResponse<BankAccount>> =>
    apiGet<PaginatedResponse<BankAccount>>(`${API_BASE}/bank-accounts${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<BankAccount>> =>
    apiGet<DataResponse<BankAccount>>(`${API_BASE}/bank-accounts/${id}`),

  create: async (data: BankAccountCreateRequest): Promise<DataResponse<BankAccount>> =>
    apiPost<DataResponse<BankAccount>>(`${API_BASE}/bank-accounts`, data),

  update: async (id: string, data: BankAccountUpdateRequest): Promise<DataResponse<BankAccount>> =>
    apiPut<DataResponse<BankAccount>>(`${API_BASE}/bank-accounts/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/bank-accounts/${id}`);
  },
};

// ============== Reimbursement payers (owner/personal & third-party cards) ==============

export const reimbursementApi = {
  listPayerBalances: async (
    params?: ListPayerBalancesParams,
  ): Promise<DataResponse<PayerBalance[]>> =>
    apiGet<DataResponse<PayerBalance[]>>(`${API_BASE}/reimbursements/payers${buildParams(params)}`),

  listPayerTransactions: async (
    payerEntityTable: PayerEntityTable,
    payerEntityId: string,
    params?: ListPayerTransactionsParams,
  ): Promise<PayerTransactionsResponse> =>
    apiGet<PayerTransactionsResponse>(
      `${API_BASE}/reimbursements/payers/${payerEntityTable}/${payerEntityId}/transactions${buildParams(params)}`,
    ),

  clear: async (
    data: ReimbursementClearRequest,
  ): Promise<DataResponse<PayerReimbursementLedgerEntry>> =>
    apiPost<DataResponse<PayerReimbursementLedgerEntry>>(`${API_BASE}/reimbursements/clear`, data),

  clearBatch: async (
    data: ReimbursementClearBatchRequest,
  ): Promise<DataResponse<PayerReimbursementLedgerEntry[]>> =>
    apiPost<DataResponse<PayerReimbursementLedgerEntry[]>>(
      `${API_BASE}/reimbursements/clear-batch`,
      data,
    ),
};

// ============== T19 — Bank Deposits / Transfers / Direct Fee / Routing ==============

const BANK_BASE = `${API_BASE}/bank`;

export const bankDepositApi = {
  list: async (params?: ListBankDepositsParams): Promise<PaginatedResponse<BankDeposit>> =>
    apiGet<PaginatedResponse<BankDeposit>>(`${BANK_BASE}/deposits${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<BankDeposit>> =>
    apiGet<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}`),

  create: async (data: BankDepositCreate): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits`, data),

  // The route is PATCH (bank_deposits_routes.update_deposit), over the same
  // BankDepositUpdate body. This was apiPut, so editing a draft deposit 405'd for
  // the feature's whole life — surfaced by the route-link gate learning to read the
  // api<Verb>() wrappers (WI 83e6b9f1), the same shape as the KB-article 405 that
  // gate was built for.
  update: async (id: string, data: BankDepositUpdate): Promise<DataResponse<BankDeposit>> =>
    apiPatch<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}`, data),

  // Backend returns the full parent deposit (BankDepositResponse), not the line.
  addLine: async (id: string, line: BankDepositLineCreate): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}/lines`, line),

  removeLine: async (id: string, lineId: string): Promise<void> => {
    await apiDelete(`${BANK_BASE}/deposits/${id}/lines/${lineId}`);
  },

  validate: async (id: string): Promise<DataResponse<BankDepositValidateResult>> =>
    apiPost<DataResponse<BankDepositValidateResult>>(`${BANK_BASE}/deposits/${id}/validate`),

  post: async (id: string, data: BankDepositPostRequest): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}/post`, data),

  void: async (id: string, data: BankDepositVoidRequest): Promise<DataResponse<BankDeposit>> =>
    apiPost<DataResponse<BankDeposit>>(`${BANK_BASE}/deposits/${id}/void`, data),

  pendingReceipts: async (params?: ListPendingReceiptsParams): Promise<DataResponse<PendingReceipt[]>> =>
    apiGet<DataResponse<PendingReceipt[]>>(`${BANK_BASE}/pending-receipts${buildParams(params)}`),

  defaultRouting: async (payment_method_kind: string): Promise<DataResponse<DepositRoutingDefault>> =>
    apiGet<DataResponse<DepositRoutingDefault>>(
      `${BANK_BASE}/routing/default${buildParams({ payment_method_kind })}`,
    ),
};

export const bankTransferApi = {
  list: async (params?: ListBankTransfersParams): Promise<PaginatedResponse<BankTransfer>> =>
    apiGet<PaginatedResponse<BankTransfer>>(`${BANK_BASE}/transfers${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<BankTransfer>> =>
    apiGet<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers/${id}`),

  create: async (data: BankTransferCreate): Promise<DataResponse<BankTransfer>> =>
    apiPost<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers`, data),

  post: async (id: string): Promise<DataResponse<BankTransfer>> =>
    apiPost<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers/${id}/post`),

  void: async (id: string, data: BankTransferVoidRequest): Promise<DataResponse<BankTransfer>> =>
    apiPost<DataResponse<BankTransfer>>(`${BANK_BASE}/transfers/${id}/void`, data),
};

export const bankFeeApi = {
  post: async (data: BankFeePostRequest): Promise<DataResponse<unknown>> =>
    apiPost<DataResponse<unknown>>(`${BANK_BASE}/post-fee`, data),

  confirmDepositMatch: async (data: ConfirmDepositMatchRequest): Promise<DataResponse<unknown>> =>
    apiPost<DataResponse<unknown>>(`${BANK_BASE}/confirm-deposit-match`, data),
};

// ============== Recurring Templates ==============

export const recurringApi = {
  list: async (params?: ListRecurringParams): Promise<PaginatedResponse<RecurringTemplate>> =>
    apiGet<PaginatedResponse<RecurringTemplate>>(`${API_BASE}/recurring${buildParams(params)}`),

  get: async (id: string): Promise<DataResponse<RecurringTemplate>> =>
    apiGet<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}`),

  create: async (data: CreateRecurringFromInvoice): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring`, data),

  createFromBill: async (
    data: CreateRecurringFromBill,
  ): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/from-bill`, data),

  update: async (id: string, data: UpdateRecurring): Promise<DataResponse<RecurringTemplate>> =>
    apiPut<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}`, data),

  // Generate this template's next document immediately (off-schedule). Returns the
  // updated template plus a reference to the created document (null if the current
  // period was already generated).
  generateNow: async (
    id: string,
  ): Promise<DataResponse<RecurringTemplate> & { generated: { id: string; type: string } | null }> =>
    apiPost<DataResponse<RecurringTemplate> & { generated: { id: string; type: string } | null }>(
      `${API_BASE}/recurring/${id}/generate-now`,
    ),

  pause: async (id: string): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}/pause`),

  resume: async (id: string): Promise<DataResponse<RecurringTemplate>> =>
    apiPost<DataResponse<RecurringTemplate>>(`${API_BASE}/recurring/${id}/resume`),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/recurring/${id}`);
  },
};
